'use client';

import React from 'react';
import { cn } from '@/app/lib';
import { Typography } from '@workspace/ui/common';
import { Radio } from '@workspace/ui/common/Radio/Radio';
import { Dropdown } from '@workspace/ui/common/Dropdowns/Dropdown';
import { Button } from '@workspace/ui/common/Buttons/Button';
import { stripInvisibles } from '@workspace/constants';
import { AttributeFilter } from '@workspace/interfaces';

interface IProductVariantProps {
  attributeFilters: AttributeFilter[];
  selectedAttributes: Record<string, string>;
  onAttributeChange: (attributeId: string, value: string) => void;
  getAvailableValues: (attributeId: string) => string[];
  getAllPossibleValues: (attributeId: string) => string[];
}

export const ProductVariantSelector = ({
  attributeFilters,
  selectedAttributes,
  onAttributeChange,
  getAvailableValues,
  getAllPossibleValues,
}: IProductVariantProps) => {
  const handleDropdownChange = (attributeId: string, value: string) => onAttributeChange(attributeId, value);
  const handleRadioChange = (attributeId: string, value: string) => onAttributeChange(attributeId, value);
  const handleTagClick = (attributeId: string, value: string) => {
    const currentValue = selectedAttributes[attributeId];
    onAttributeChange(attributeId, currentValue === value ? '' : value);
  };

  if (!attributeFilters || attributeFilters.length === 0) return null;

  return (
    <div className="product-variant-container flex flex-col gap-4">
      {attributeFilters.map((filter) => {
        const availableValues = getAvailableValues(filter.attribute._ref);
        const allPossibleValues = getAllPossibleValues(filter.attribute._ref);
        const selectedValue = selectedAttributes[filter.attribute._ref];
        const displayName = filter.displayText || filter?.attribute?.name;

        if (allPossibleValues.length === 0) return null;

        return (
          <div key={filter._key} className="flex flex-col">
            {filter.displayType === 'dropdown' ? (
              <Dropdown
                label={displayName}
                placeholder="Please Select"
                options={[
                  ...allPossibleValues.map((value) => ({
                    label: value,
                    value,
                    disabled: !availableValues.includes(value),
                  })),
                ]}
                value={selectedValue || ''}
                onChange={(v) => handleDropdownChange(filter.attribute._ref, v)}
                className="w-full"
                labelClassName="!text-secondary-400 !font-openSans !text-sm md:!text-base !font-bold md:!font-semibold !leading-6 !mb-2"
              />
            ) : (
              <Typography
                variant="h4"
                className="text-secondary-400 font-openSans! text-sm! md:text-base! font-bold md:font-semibold! leading-[150%] mb-2!"
              >
                {displayName}
              </Typography>
            )}

            {filter.displayType === 'radioButton' ? (
              <div className="flex gap-lg md:gap-x-xl gap-y-2 flex-wrap items-center">
                {allPossibleValues.map((value) => {
                  const isSelected = selectedValue === value;
                  const isDisabled = !availableValues.includes(value);
                  const label = stripInvisibles(value).trim();

                  return (
                    <Radio
                      key={value}
                      value={value}
                      checked={isSelected}
                      onChange={() => !isDisabled && handleRadioChange(filter.attribute._ref, value)}
                      status={isDisabled ? 'disabled' : 'active'}
                      className={cn(
                        'flex items-center gap-3 transition-colors w-max',
                        isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                      )}
                      label={label}
                      labelClassName={cn(
                        'font-openSans leading-6 text-base text-secondary-400',
                        isSelected ? 'font-semibold' : 'font-normal',
                      )}
                    />
                  );
                })}
              </div>
            ) : null}

            {filter.displayType === 'selection' ? (
              <div className="flex gap-xs flex-wrap items-stretch">
                {allPossibleValues.map((value) => {
                  const isSelected = selectedValue === value;
                  const isDisabled = !availableValues.includes(value);
                  const label = stripInvisibles(value).trim();

                  return (
                    <Button
                      key={value}
                      variant="secondary"
                      isDisabled={isDisabled}
                      onClick={() => !isDisabled && handleTagClick(filter.attribute._ref, value)}
                      className={cn(
                        'px-3! py-2 rounded-sm h-10 flex-1 basis-0 w-max min-w-max! max-w-max! ring-0! focus:ring-0! outline-none! focus:outline-none! cursor-pointer focus:ring-offset-0! border! leading-md!',
                        // Override visuals to match specification
                        isSelected
                          ? 'border-primary! bg-white!'
                          : isDisabled
                            ? 'bg-secondary-100! cursor-not-allowed border-none!'
                            : 'bg-white! border-secondary-100! hover:bg-secondary-50!',
                      )}
                    >
                      <Typography
                        variant="bodySm"
                        className={cn(
                          'text-base! whitespace-nowrap leading-md! font-openSans',
                          isSelected ? 'font-bold! text-primary!' : 'font-normal!',
                          isDisabled ? 'text-secondary-200 font-bold!' : 'text-secondary-400',
                        )}
                      >
                        {label}
                      </Typography>
                    </Button>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
